import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { apiService } from '../../components/Config/api.service';
import { enveloppeDe } from '../../components/Utils/enveloppeDe';
import { notify } from '../../components/Utils/Notifications';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE } from '../Employee/organigrammeOutils';
import { CLASSE_CHAMP } from './departOutils';
import FormulaireConvocation from './FormulaireConvocation';
import { EtatDossier, messageRefus, lireCopieSignee, cleDelai } from './disciplineOutils';

// Actions d'écriture par `etat` reçu de l'étape (décisions 11 et 15) ; `annulee` et un
// état inconnu n'en ont aucune. Les téléchargements suivent les flags `pdf`/`docx`.
const ACTIONS_ETAPE = { brouillon: ['emettre', 'annuler'], emise: ['remettre', 'deposer', 'annuler'], remise: ['deposer', 'annuler'] };

/**
 * Le contenu du volet d'un dossier (C39 `clore`, `annuler`, `etapes/*`). Il lit le
 * dossier reçu en prop : aucune route ne lit un dossier seul. Les actions suivent l'`etat`
 * reçu d'Odoo, jamais un calcul du portail. Les étapes sont dans l'ordre reçu.
 */
function PanneauDossierDisciplinaire({ dossier, occupe, onDossier, onRecharger, onEtape }) {
  const { t } = useTranslation();
  const [annulation, setAnnulation] = useState(false);
  const [motif, setMotif] = useState('');
  const [annulationEtapeId, setAnnulationEtapeId] = useState(null);
  const [motifEtape, setMotifEtape] = useState('');
  const [actionEnVol, setActionEnVol] = useState(false);
  // Une seule action à la fois, garde synchrone en plus du `disabled`.
  const enVol = useRef(false);
  const [remiseEtapeId, setRemiseEtapeId] = useState(null);
  const [dateRemise, setDateRemise] = useState('');
  const champs = useRef({});

  // `appliquer` reçoit la réponse : par défaut la ligne est remplacée par le dossier
  // d'Odoo (par son `id`), jamais recalculée. Rend `reponse.ok` (le formulaire s'en sert).
  const agir = async (appel, cleSucces, { contexte, appliquer = (r) => onDossier(r.dossier) } = {}) => {
    if (enVol.current) return false;
    enVol.current = true;
    setActionEnVol(true);
    const reponse = await enveloppeDe(appel);
    enVol.current = false;
    setActionEnVol(false);
    if (reponse.ok) {
      appliquer(reponse);
      if (cleSucces) notify(t(cleSucces), '', 2);
    } else {
      // Un refus (`dossier_clos`...) dit que la ligne est périmée : on recharge.
      notify(messageRefus(reponse, t, contexte), '', 3);
      onRecharger();
    }
    return Boolean(reponse.ok);
  };

  const clore = () => {
    if (enVol.current) return;
    if (!window.confirm(t('discipline.confirmerClore'))) return;
    agir(() => apiService.cloreDossierDisciplinaire(dossier.id), 'discipline.clos');
  };
  const annuler = () => agir(() => apiService.annulerDossierDisciplinaire(dossier.id, motif.trim()), 'discipline.annule');

  const emettre = (etape) => {
    if (enVol.current) return;
    if (!window.confirm(t('discipline.confirmerEmettre'))) return;
    agir(() => apiService.emettreEtapeDisciplinaire(etape.id), 'discipline.etapeEmise',
      { appliquer: (r) => onEtape(dossier.id, r.etape) });
  };
  // Un seul motif pour tout le volet : il repart de zéro à chaque étape ouverte.
  const ouvrirAnnulationEtape = (etape) => {
    setMotifEtape('');
    setAnnulationEtapeId(etape.id);
  };
  const annulerEtape = (etape) => agir(
    () => apiService.annulerEtapeDisciplinaire(etape.id, motifEtape.trim()), 'discipline.etapeAnnulee'
  );
  // Date saisie sans `max` : « aujourd'hui » est celui d'Odoo, c'est lui qui refuse (valeurs_invalides).
  const ouvrirRemise = (etape) => { setDateRemise(''); setRemiseEtapeId(etape.id); };
  const remettre = (etape) => agir(() => apiService.remettreEtapeDisciplinaire(etape.id, dateRemise), 'discipline.etapeRemise',
    { contexte: 'remettre', appliquer: (r) => onEtape(dossier.id, r.etape) });
  // Remplacer une copie signée exige une confirmation avant même d'ouvrir le sélecteur.
  const ouvrirSelecteur = (etape) => {
    if (enVol.current) return;
    if (etape.signe === true && !window.confirm(t('discipline.confirmerRemplacerCopie'))) return;
    champs.current[etape.id].click();
  };
  // La lecture est sous la garde `enVol` ; un refus local suit le chemin d'un refus d'Odoo.
  const deposer = (etape, fichier) => agir(async () => {
    const b = await lireCopieSignee(fichier);
    return b ? apiService.deposerCopieSigneeDisciplinaire(etape.id, b) : { ok: false, motif: 'fichier_invalide' };
  }, 'discipline.copieDeposee', { contexte: 'document', appliquer: (r) => onEtape(dossier.id, r.etape) });
  const telecharger = (etape, format) => agir(
    () => apiService.telechargerEtapeDisciplinaire(etape, dossier.numero, format), null,
    { contexte: 'telecharger', appliquer: () => {} }
  );

  const bloque = occupe || actionEnVol;
  const ouvert = dossier.etat === 'ouvert';
  return (
    <div data-testid="dossier-panneau" className="space-y-4 text-sm">
      <div className="flex items-center gap-3">
        <h2 data-testid="dossier-numero" className="text-lg font-semibold">{dossier.numero}</h2>
        <EtatDossier etat={dossier.etat} />
      </div>
      <p>{dossier.employe?.nom} · {dossier.date_faits}</p>
      <p data-testid="dossier-cree-par" className="text-gray-500">{t('discipline.creePar')} {dossier.cree_par}</p>
      <p data-testid="dossier-faits" className="whitespace-pre-line">{dossier.faits}</p>
      {dossier.motif_annulation != null && (
        <p data-testid="dossier-motif-annulation">{t('discipline.motifAnnulationLu')} {dossier.motif_annulation}</p>
      )}

      <h3 className="font-semibold">{t('discipline.etapes')}</h3>
      {(dossier.etapes || []).length === 0 ? (
        <p className="text-gray-500">{t('discipline.aucuneEtape')}</p>
      ) : (
        <ul className="divide-y divide-gray-200">
          {dossier.etapes.map((etape) => {
            const actions = ouvert ? ACTIONS_ETAPE[etape.etat] || [] : [];
            return (
              <li key={etape.id} data-testid={`dossier-etape-${etape.id}`} className="py-2 grid grid-cols-2 gap-1">
                <span data-testid={`dossier-etape-type-${etape.id}`}>{t(`discipline.types.${etape.type}`, { defaultValue: etape.type })}</span>
                <span data-testid={`dossier-etape-etat-${etape.id}`}>{t(`discipline.etatsEtape.${etape.etat}`, { defaultValue: etape.etat })}</span>
                <span><span className="text-gray-500">{t('discipline.colEcheance')} </span><span data-testid={`dossier-etape-echeance-${etape.id}`}>{etape.echeance}</span></span>
                <span><span className="text-gray-500">{t('discipline.heure')} </span><span data-testid={`dossier-etape-heure-${etape.id}`}>{etape.heure ?? '—'}</span></span>
                <span><span className="text-gray-500">{t('discipline.dateRemise')} </span><span data-testid={`dossier-etape-date-remise-${etape.id}`}>{etape.date_remise ?? '—'}</span></span>
                <span data-testid={`dossier-etape-delai-${etape.id}`} className="text-gray-500">{t('discipline.delai.' + cleDelai(etape.delai_respecte))}</span>
                <span className="col-span-2"><span className="text-gray-500">{t('discipline.lieu')} </span><span data-testid={`dossier-etape-lieu-${etape.id}`}>{etape.lieu}</span></span>
                <span className="col-span-2 flex flex-wrap gap-2">
                  {actions.includes('emettre') && (
                    <button type="button" data-testid={`etape-emettre-${etape.id}`} disabled={bloque} className={BOUTON_PRINCIPAL}
                      onClick={() => emettre(etape)}>{t('discipline.emettre')}</button>
                  )}
                  {actions.includes('remettre') && (
                    <button type="button" data-testid={`etape-remettre-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => ouvrirRemise(etape)}>{t('discipline.remettre')}</button>
                  )}
                  {actions.includes('deposer') && (<>
                    <button type="button" data-testid={`etape-deposer-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => ouvrirSelecteur(etape)}>{t(etape.signe === true ? 'discipline.remplacerCopie' : 'discipline.deposerCopie')}</button>
                    <input type="file" accept="application/pdf" className="hidden" data-testid={`etape-fichier-${etape.id}`} disabled={bloque}
                      ref={(el) => { champs.current[etape.id] = el; }}
                      onChange={(e) => { const f = e.target.files[0]; if (f) deposer(etape, f); e.target.value = ''; }} />
                  </>)}
                  {etape.signe === true && (
                    <button type="button" data-testid={`etape-signe-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => telecharger(etape, 'signe')}>{t('discipline.telechargerSigne')}</button>
                  )}
                  {actions.includes('annuler') && (
                    <button type="button" data-testid={`etape-annuler-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => ouvrirAnnulationEtape(etape)}>{t('discipline.annulerEtape')}</button>
                  )}
                  {/* Décision 17 : télécharger reste possible quel que soit l'état du dossier ou de l'étape. */}
                  {etape.pdf === true && (
                    <button type="button" data-testid={`etape-pdf-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => telecharger(etape, 'pdf')}>{t('discipline.telechargerPdf')}</button>
                  )}
                  {etape.docx === true && (
                    <button type="button" data-testid={`etape-docx-${etape.id}`} disabled={bloque} className={BOUTON_SECONDAIRE}
                      onClick={() => telecharger(etape, 'docx')}>{t('discipline.telechargerDocx')}</button>
                  )}
                </span>
                {actions.includes('remettre') && remiseEtapeId === etape.id && (
                  <span className="col-span-2 space-y-2">
                    <label className="block font-medium">
                      {t('discipline.dateRemise')}
                      <input type="date" data-testid={`etape-date-remise-${etape.id}`} value={dateRemise}
                        onChange={(e) => setDateRemise(e.target.value)} className={CLASSE_CHAMP} />
                    </label>
                    <button type="button" data-testid={`etape-remettre-confirmer-${etape.id}`} disabled={bloque || !dateRemise}
                      className={BOUTON_PRINCIPAL} onClick={() => remettre(etape)}>{t('discipline.confirmerRemise')}</button>
                  </span>
                )}
                {actions.includes('annuler') && annulationEtapeId === etape.id && (
                  <span className="col-span-2 space-y-2">
                    <label className="block font-medium">
                      {t('discipline.motifAnnulationEtape')}
                      <input type="text" data-testid={`etape-motif-${etape.id}`} value={motifEtape}
                        onChange={(e) => setMotifEtape(e.target.value)} className={CLASSE_CHAMP} />
                    </label>
                    <button type="button" data-testid={`etape-annuler-confirmer-${etape.id}`} disabled={bloque || !motifEtape.trim()}
                      className={BOUTON_PRINCIPAL} onClick={() => annulerEtape(etape)}>
                      {t('discipline.confirmerAnnulationEtape')}
                    </button>
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {ouvert && (
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <FormulaireConvocation bloque={bloque}
            onEnvoyer={({ echeance, heure, lieu }) => agir(
              () => apiService.ajouterConvocationDisciplinaire(dossier.id, echeance, heure, lieu), 'discipline.convocationAjoutee'
            )} />
          <div className="flex gap-3">
            <button type="button" data-testid="dossier-clore" disabled={bloque} className={BOUTON_PRINCIPAL} onClick={clore}>
              {t('discipline.clore')}
            </button>
            <button type="button" data-testid="dossier-annuler" disabled={bloque} className={BOUTON_SECONDAIRE}
              onClick={() => setAnnulation(true)}>
              {t('discipline.annulerDossier')}
            </button>
          </div>
          {annulation && (
            <div className="space-y-2">
              <label className="block font-medium">
                {t('discipline.motifAnnulation')}
                <textarea data-testid="dossier-motif" rows={3} value={motif}
                  onChange={(e) => setMotif(e.target.value)} className={CLASSE_CHAMP} />
              </label>
              <button type="button" data-testid="dossier-annuler-confirmer" disabled={bloque || !motif.trim()}
                className={BOUTON_PRINCIPAL} onClick={annuler}>
                {t('discipline.confirmerAnnulation')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

PanneauDossierDisciplinaire.propTypes = {
  dossier: PropTypes.object.isRequired,
  occupe: PropTypes.bool,
  onDossier: PropTypes.func.isRequired,
  onRecharger: PropTypes.func.isRequired,
  onEtape: PropTypes.func.isRequired,
};

export default PanneauDossierDisciplinaire;
